export const MATRIX_ROWS = [
  { key: 'exposureBeginningBalance', label: 'Exposure Beginning Balance' },
  { key: 'growthInvestments', label: 'Growth Investments' },
  { key: 'fee', label: 'Fee' },
  { key: 'returns', label: 'Returns' },
  { key: 'facilityFee', label: 'Facility Fee' },
  { key: 'facilityFeePayment', label: 'Facility Fee Payment' },
  { key: 'exposureEndingBalance', label: 'Exposure Ending Balance' },
];

/** Line items shown as cash inflows (negative) in the matrix / export. */
export const NEGATIVE_DISPLAY_KEYS = new Set(['returns', 'facilityFeePayment']);

/** Display sign for matrix cells; stored values stay non-negative for balance math. */
export function displaySignedAmount(key, value) {
  if (value == null || value === 0) return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  if (NEGATIVE_DISPLAY_KEYS.has(key) && n > 0) return -n;
  return n;
}

/** Finance-style currency: negatives as ($1,234), not red / minus. */
export function formatAccountingCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencySign: 'accounting',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Number(value));
}

export const FUNDING_CONSTRAINT_LABELS = {
  max_exposure: 'Max exposure limit',
  max_funding_amount: 'Max funding amount limit',
  max_funding_pct: 'Max funding percentage limit',
  max_mom_growth_pct: 'Max month-over-month growth % limit',
  max_mom_funding_amount: 'Max month-over-month funding amount limit',
  target_xirr: 'Target XIRR constraint',
  covenant: 'Deal covenant',
  no_feasible_funding: 'No feasible funding under current limits',
};

/** True when funding was reduced below the intended request by a covenant/limit. */
export function isFundingConstraintIssue(code) {
  if (!code || code === 'zero_marketing') return false;
  return Object.prototype.hasOwnProperty.call(FUNDING_CONSTRAINT_LABELS, code);
}

export function formatFundingConstraintLabel(code) {
  if (!code) return '';
  return FUNDING_CONSTRAINT_LABELS[code] || code.replace(/_/g, ' ');
}

export function formatGrowthConstraintTooltip(month, { includeCustomerNames = false } = {}) {
  const issues = [];
  if (Array.isArray(month?.fundingConstraints) && month.fundingConstraints.length) {
    month.fundingConstraints.forEach((issue) => {
      if (!isFundingConstraintIssue(issue.bindingConstraint)) return;
      const reason = formatFundingConstraintLabel(issue.bindingConstraint);
      if (includeCustomerNames && issue.companyName) {
        issues.push(`${issue.companyName}: ${reason}`);
      } else {
        issues.push(reason);
      }
    });
  } else if (isFundingConstraintIssue(month?.bindingConstraint)) {
    issues.push(formatFundingConstraintLabel(month.bindingConstraint));
  }
  if (!issues.length) return '';
  return `Requested funding could not be fully provided.\n${issues.join('\n')}`;
}

export function monthKeyFromDate(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function nextMonthKey(month) {
  const [year, monthNumber] = String(month || '').split('-').map(Number);
  if (!year || !monthNumber) return '';
  return monthKeyFromDate(new Date(Date.UTC(year, monthNumber, 1)));
}

// First month after the current UTC month. The chart line sits on its left edge,
// between the month we are in and the following month.
export function forecastStartMonth(now = new Date()) {
  return nextMonthKey(monthKeyFromDate(now));
}

export function filterMonthsByDateRange(months, startMonth, endMonth) {
  if (!months?.length) return [];
  return months.filter((m) => {
    if (startMonth && m.month < startMonth) return false;
    if (endMonth && m.month > endMonth) return false;
    return true;
  });
}

export function aggregateSeries(customers) {
  if (!customers?.length) return [];
  const index = new Map();
  const months = [];
  customers.forEach((customer) => {
    (customer.months || []).forEach((row) => {
      let i = index.get(row.month);
      if (i == null) {
        i = months.length;
        index.set(row.month, i);
        months.push({
          month: row.month,
          isProjected: !!row.isProjected,
          exposureBeginningBalance: 0,
          exposureBeginningBalanceWithFacility: 0,
          growthInvestments: 0,
          returns: 0,
          returnsBreakdown: [],
          fee: 0,
          feeBreakdown: [],
          facilityFee: 0,
          facilityFeePayment: 0,
          exposureEndingBalance: 0,
          exposureEndingBalanceWithFacility: 0,
          fundingConstraints: [],
        });
      }
      const target = months[i];
      target.exposureBeginningBalance += row.exposureBeginningBalance || 0;
      target.exposureBeginningBalanceWithFacility += row.exposureBeginningBalanceWithFacility || 0;
      target.growthInvestments += row.growthInvestments || 0;
      target.returns += row.returns || 0;
      target.returnsBreakdown = mergeReturnsBreakdown(target.returnsBreakdown, row.returnsBreakdown);
      target.fee += row.fee || 0;
      target.feeBreakdown = mergeReturnsBreakdown(target.feeBreakdown, row.feeBreakdown);
      target.facilityFee += row.facilityFee || 0;
      target.facilityFeePayment += row.facilityFeePayment || 0;
      target.exposureEndingBalance += row.exposureEndingBalance || 0;
      target.exposureEndingBalanceWithFacility += row.exposureEndingBalanceWithFacility || 0;
      target.isProjected = target.isProjected || !!row.isProjected;
      if (isFundingConstraintIssue(row.bindingConstraint)) {
        target.fundingConstraints.push({
          companyName: customer.companyName || customer.customerId || 'Customer',
          bindingConstraint: row.bindingConstraint,
          growthInvestments: row.growthInvestments || 0,
        });
        if (!target.bindingConstraint) {
          target.bindingConstraint = row.bindingConstraint;
        }
      }
    });
  });
  months.sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
  return months;
}

function breakdownKey(b) {
  return `${b.source || ''}|${b.cohortMonth || ''}|${b.label || ''}`;
}

export function mergeReturnsBreakdown(dst, src) {
  const out = [...(dst || [])];
  const index = new Map(out.map((b, i) => [breakdownKey(b), i]));
  (src || []).forEach((b) => {
    const key = breakdownKey(b);
    if (index.has(key)) {
      const i = index.get(key);
      out[i] = { ...out[i], amount: (out[i].amount || 0) + (b.amount || 0) };
      return;
    }
    index.set(key, out.length);
    out.push({ ...b });
  });
  return out;
}

export function displayCohortBreakdownLabel(item) {
  const raw = item?.label || item?.cohortMonth || item?.source || '';
  const cleaned = String(raw).replace(/\s*\(SQL\)\s*/gi, '').trim();
  if (cleaned) return cleaned;
  if (item?.source === 'funded_sql') return 'Funded cohorts';
  return 'Cohort';
}

export function formatCohortBreakdownTooltip(
  breakdown,
  { includeRoasPeriod = false, asNegative = false } = {}
) {
  if (!breakdown?.length) return '';
  return [...breakdown]
    .filter((b) => b && (b.amount || 0) !== 0)
    .sort((a, b) => String(a.cohortMonth || '').localeCompare(String(b.cohortMonth || '')))
    .map((b) => {
      let n = Number(b.amount || 0);
      if (asNegative && n > 0) n = -n;
      const amount = asNegative
        ? formatAccountingCurrency(n)
        : n.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          });
      const period =
        includeRoasPeriod && b.roasPeriod != null && b.roasPeriod >= 0
          ? ` (ROAS p${b.roasPeriod})`
          : '';
      return `${displayCohortBreakdownLabel(b)}${period}: ${amount}`;
    })
    .join('\n');
}

export function formatReturnsBreakdownTooltip(breakdown) {
  return formatCohortBreakdownTooltip(breakdown, { includeRoasPeriod: true, asNegative: true });
}

export function formatFeeBreakdownTooltip(breakdown) {
  return formatCohortBreakdownTooltip(breakdown);
}

export function resolveHorizon(months) {
  if (!months?.length) return { startMonth: '', endMonth: '' };
  return {
    startMonth: months[0].month,
    endMonth: months[months.length - 1].month,
  };
}

export function formatMonthLabel(monthKey) {
  if (!monthKey || monthKey.length < 7) return monthKey || '';
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Excel accounting currency: $1,234 / ($1,234) */
export const EXCEL_MONEY_NUM_FMT = '$#,##0;($#,##0)';

/**
 * @param {object[]} months
 * @param {{ currencyAsString?: boolean }} [options]
 *   currencyAsString — CSV-friendly "$1,234" / "($1,234)"; else numeric values for Excel numFmt.
 */
export function buildExportRows(months, { currencyAsString = false } = {}) {
  const header = ['Line Item', ...(months || []).map((m) => formatMonthLabel(m.month))];
  const rows = MATRIX_ROWS.map((def) => [
    def.label,
    ...(months || []).map((m) => {
      const v = m[def.key];
      if (v == null || v === 0) return '';
      const signed = NEGATIVE_DISPLAY_KEYS.has(def.key)
        ? displaySignedAmount(def.key, v)
        : Number(v);
      if (currencyAsString) return formatAccountingCurrency(signed);
      return signed;
    }),
  ]);
  return [header, ...rows];
}

/** Safe unique Excel worksheet name (max 31 chars). */
export function excelSheetName(name, usedNames) {
  const used = usedNames || new Set();
  let base = String(name || 'Sheet')
    .replace(/[\\/*?:\[\]]/g, '')
    .trim()
    .slice(0, 31);
  if (!base) base = 'Sheet';
  let candidate = base;
  let i = 2;
  while (used.has(candidate)) {
    const suffix = ` (${i})`;
    candidate = `${base.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`;
    i += 1;
  }
  used.add(candidate);
  return candidate;
}

/**
 * @param {import('exceljs').Worksheet} sheet
 * @param {object[]} months
 * @param {{ includeGrowthConstraintNotes?: boolean }} [options]
 *   includeGrowthConstraintNotes — Excel cell comments on Growth Investments when a covenant binds (per-customer tabs).
 */
export function writeCashFlowExportSheet(sheet, months, { includeGrowthConstraintNotes = false } = {}) {
  const rows = buildExportRows(months, { currencyAsString: false });
  rows.forEach((row, rowIndex) => {
    const excelRow = sheet.addRow(row);
    if (rowIndex === 0) {
      excelRow.font = { bold: true };
      return;
    }
    excelRow.eachCell((cell, colNumber) => {
      if (colNumber === 1) return;
      if (typeof cell.value === 'number') {
        cell.numFmt = EXCEL_MONEY_NUM_FMT;
      }
    });
  });
  sheet.getColumn(1).width = 40;
  for (let c = 2; c <= (rows[0]?.length || 1); c += 1) {
    sheet.getColumn(c).width = 14;
  }

  if (!includeGrowthConstraintNotes || !months?.length) return;

  const growthRowNumber = MATRIX_ROWS.findIndex((r) => r.key === 'growthInvestments') + 2;
  if (growthRowNumber < 2) return;

  months.forEach((month, monthIndex) => {
    const tip = formatGrowthConstraintTooltip(month, { includeCustomerNames: false });
    if (!tip) return;
    const cell = sheet.getRow(growthRowNumber).getCell(monthIndex + 2);
    cell.note = tip;
    if (typeof cell.value === 'number') {
      cell.numFmt = EXCEL_MONEY_NUM_FMT;
    }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF6C9A0' },
    };
  });
}

export function writeCohortRepaymentExportSheet(sheet, months) {
  const rows = buildCohortRepaymentVectorRows(months);
  const moneyFromCol = 4; // after Cohort, Source, Funding Month
  rows.forEach((row, rowIndex) => {
    const excelRow = sheet.addRow(row);
    if (rowIndex === 0) {
      excelRow.font = { bold: true };
      return;
    }
    excelRow.eachCell((cell, colNumber) => {
      if (colNumber < moneyFromCol) return;
      if (typeof cell.value === 'number') {
        // Cohort repayments are cash inflows → show as accounting negatives.
        const n = cell.value;
        cell.value = n > 0 ? -n : n;
        cell.numFmt = EXCEL_MONEY_NUM_FMT;
      }
    });
  });
  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 14;
  sheet.getColumn(3).width = 14;
}

function cohortBreakdownKey(b) {
  return `${b.source || ''}|${b.cohortMonth || ''}|${b.label || ''}`;
}

/**
 * Builds a matrix of repayment amounts by cohort (rows) × calendar month (columns).
 * Sourced from each month's returnsBreakdown (SQL funded + projected cohorts).
 */
export function buildCohortRepaymentVectorRows(months) {
  const list = months || [];
  const cohortOrder = [];
  const cohortMeta = new Map();

  list.forEach((m) => {
    (m.returnsBreakdown || []).forEach((b) => {
      const key = cohortBreakdownKey(b);
      if (!cohortMeta.has(key)) {
        cohortMeta.set(key, {
          key,
          label: displayCohortBreakdownLabel(b),
          source: b.source || '',
          cohortMonth: b.cohortMonth || '',
        });
        cohortOrder.push(key);
      }
    });
  });

  cohortOrder.sort((a, b) => {
    const ca = cohortMeta.get(a);
    const cb = cohortMeta.get(b);
    if (ca.source !== cb.source) {
      if (ca.source === 'funded_sql') return -1;
      if (cb.source === 'funded_sql') return 1;
    }
    return String(ca.cohortMonth).localeCompare(String(cb.cohortMonth));
  });

  const header = [
    'Cohort',
    'Source',
    'Funding Month',
    ...list.map((m) => formatMonthLabel(m.month)),
    'Total',
  ];

  const rows = cohortOrder.map((key) => {
    const meta = cohortMeta.get(key);
    let total = 0;
    const monthVals = list.map((m) => {
      const match = (m.returnsBreakdown || []).find((b) => cohortBreakdownKey(b) === key);
      const amount = match?.amount || 0;
      total += amount;
      return amount === 0 ? '' : amount;
    });
    return [
      meta.label,
      meta.source,
      meta.cohortMonth ? formatMonthLabel(meta.cohortMonth) : '',
      ...monthVals,
      total === 0 ? '' : total,
    ];
  });

  if (rows.length) {
    const totals = list.map((m) => {
      const sum = (m.returnsBreakdown || []).reduce((acc, b) => acc + (b.amount || 0), 0);
      return sum === 0 ? '' : sum;
    });
    const grand = list.reduce(
      (acc, m) => acc + (m.returnsBreakdown || []).reduce((a, b) => a + (b.amount || 0), 0),
      0
    );
    rows.push(['Total', '', '', ...totals, grand === 0 ? '' : grand]);
  }

  return [header, ...rows];
}

export function toCsv(rows) {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          const s = cell == null ? '' : String(cell);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(',')
    )
    .join('\n');
}
